import {useState} from 'react';

import type {CustomerInsightsViewModel} from '../types/CustomerInsightsViewModel';
import {buildSectionState} from '../utils/buildSectionState';
import {createCustomerInsightsSectionPolicy} from '../utils/createCustomerInsightsSectionPolicy';
import {getCustomerInsightsEntitledQueryState} from '../utils/getCustomerInsightsEntitledQueryState';
import {getNewestEligibleCustomerMessage} from '../utils/getNewestEligibleCustomerMessage';
import {mapCustomerIdentity} from '../utils/mapCustomerIdentity';
import {mapLastContact} from '../utils/mapLastContact';
import {useCustomerInsightsQueries} from './useCustomerInsightsQueries';

interface UseCustomerInsightsArgs {
  customerId: string;
  enabled: boolean;
}

const INITIAL_COMBINED_HISTORY_ROWS_LIMIT = 5;
const ADDITIONAL_COMBINED_HISTORY_ROWS_LIMIT = 10;

export function useCustomerInsights(args: UseCustomerInsightsArgs): CustomerInsightsViewModel {
  const [combinedHistoryPagination, setCombinedHistoryPagination] = useState({
    customerId: args.customerId,
    rowsLimit: INITIAL_COMBINED_HISTORY_ROWS_LIMIT,
  });
  const combinedHistoryRowsLimit =
    combinedHistoryPagination.customerId === args.customerId
      ? combinedHistoryPagination.rowsLimit
      : INITIAL_COMBINED_HISTORY_ROWS_LIMIT;
  const expandCombinedHistoryRowsLimit = () => {
    setCombinedHistoryPagination((currentPagination) => ({
      customerId: args.customerId,
      rowsLimit:
        (currentPagination.customerId === args.customerId
          ? currentPagination.rowsLimit
          : INITIAL_COMBINED_HISTORY_ROWS_LIMIT) + ADDITIONAL_COMBINED_HISTORY_ROWS_LIMIT,
    }));
  };
  const resetCombinedHistoryRowsLimit = () =>
    setCombinedHistoryPagination({
      customerId: args.customerId,
      rowsLimit: INITIAL_COMBINED_HISTORY_ROWS_LIMIT,
    });
  const {
    entitlements,
    customer: customerQuery,
    latestContact: latestContactQuery,
    grids,
    overdueInvoiceHref,
    overdueTaskHref,
    customerInvoicesHref,
    customerTasksHref,
    isRefreshing,
  } = useCustomerInsightsQueries(args);
  const {
    openInterests: openInterestsQuery,
    openBusinessCases: openBusinessCasesQuery,
    unsuccessfulInterests: unsuccessfulInterestsQuery,
    unsuccessfulBusinessCases: unsuccessfulBusinessCasesQuery,
    pastSales: pastSalesQuery,
    pastPurchases: pastPurchasesQuery,
    openServiceCases: openServiceCasesQuery,
    serviceHistory: serviceHistoryQuery,
    overdueInvoices: overdueInvoicesQuery,
    overdueTasks: overdueTasksQuery,
  } = grids;
  const sectionPolicy = createCustomerInsightsSectionPolicy({
    enabled: args.enabled,
    entitlements,
    expandCombinedHistoryRowsLimit,
    resetCombinedHistoryRowsLimit,
  });
  const latestContactState = getCustomerInsightsEntitledQueryState({
    enabled: args.enabled,
    entitled: entitlements.communication,
    entitlementStatus: entitlements.status,
    entitlementRetry: entitlements.retry,
    isLoading:
      latestContactQuery.isLoading ||
      (latestContactQuery.isFetching && latestContactQuery.currentData === undefined),
    isError: latestContactQuery.isError,
    retry: latestContactQuery.refetch,
  });
  const lastContact = mapLastContact(
    getNewestEligibleCustomerMessage(latestContactQuery.currentData?.messages),
    args.customerId
  );
  return {
    isRefreshing,
    identity: buildSectionState({
      data:
        customerQuery.currentData === undefined
          ? undefined
          : mapCustomerIdentity(customerQuery.currentData),
      isEmpty: () => false,
      isError: customerQuery.isError,
      isLoading:
        customerQuery.isLoading ||
        (customerQuery.isFetching && customerQuery.currentData === undefined),
      isOmitted: !args.enabled,
      retry: customerQuery.refetch,
    }),
    lastContact: buildSectionState({
      data: lastContact,
      isEmpty: () => false,
      ...latestContactState,
    }),
    overdueInvoices: sectionPolicy.alert({
      query: overdueInvoicesQuery,
      kind: 'overdueInvoices',
      href: overdueInvoiceHref,
      additionalItemsHref: customerInvoicesHref,
      entitled: entitlements.invoices,
    }),
    overdueTasks: sectionPolicy.alert({
      query: overdueTasksQuery,
      kind: 'overdueTasks',
      href: overdueTaskHref,
      additionalItemsHref: customerTasksHref,
      entitled: entitlements.tasks,
    }),
    openInterests: sectionPolicy.records({
      query: openInterestsQuery,
      kind: 'interest',
      entitled: entitlements.interests,
    }),
    openBusinessCases: sectionPolicy.records({
      query: openBusinessCasesQuery,
      kind: 'businessCase',
      entitled: entitlements.businessCases,
    }),
    openServiceCases: sectionPolicy.records({
      query: openServiceCasesQuery,
      kind: 'serviceCase',
      entitled: entitlements.serviceCases,
    }),
    pastSales: sectionPolicy.history({
      query: pastSalesQuery,
      kind: 'businessCase',
      entitled: entitlements.businessCaseSales,
      businessCaseHistoryKind: 'sales',
    }),
    pastPurchases: sectionPolicy.history({
      query: pastPurchasesQuery,
      kind: 'businessCase',
      entitled: entitlements.businessCasePurchases,
      businessCaseHistoryKind: 'purchases',
    }),
    unsuccessfulInterests: sectionPolicy.combinedHistory({
      first: {
        query: unsuccessfulInterestsQuery,
        kind: 'interest',
        entitled: entitlements.interests,
      },
      second: {
        query: unsuccessfulBusinessCasesQuery,
        kind: 'businessCase',
        entitled: entitlements.businessCases,
      },
      requestedRowsLimit: combinedHistoryRowsLimit,
    }),
    serviceHistory: sectionPolicy.history({
      query: serviceHistoryQuery,
      kind: 'serviceCase',
      entitled: entitlements.serviceCases,
    }),
  };
}
